import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Alert, CreateAlertPayload } from "../../types/Alert";
import useClientAlertsService from "../../services/administration/useClientAlertsService";
import useToast, { ToastType } from "../../hooks/useToast";
import { useTranslation } from "react-i18next";

type ClientAlertsContextType = {
    alerts: Alert[];
    alertsLoading: boolean;
    handleDeleteAlert: (id: number) => void;
    handleUpdateAlert: (newAlert: Partial<Alert>) => Promise<Alert | undefined>;
    handleCreateAlert: (newAlert: CreateAlertPayload) => Promise<void>;
    handleMarkAlertAsResolved: (id: number) => Promise<void>;
};

const ClientAlertsContext = createContext<ClientAlertsContextType>({
    alerts: [],
    alertsLoading: false,
    handleDeleteAlert: async () => { },
    handleUpdateAlert: async () => undefined,
    handleCreateAlert: async () => { },
    handleMarkAlertAsResolved: async () => { },
});

type ClientAlertsProviderProps = {
    children: ReactNode;
};

export function ClientAlertsProvider({ children }: ClientAlertsProviderProps) {
    const { fetchAllAlerts, deleteAlert, updateAlert, createAlert } = useClientAlertsService();
    const { t } = useTranslation("common");
    const { showToast } = useToast();

    const [alerts, setAlerts] = useState<Alert[]>([]);
    const [alertsLoading, setAlertsLoading] = useState<boolean>(true);

    useEffect(() => {
        setAlertsLoading(true);
        fetchAllAlerts().then((resp) => {
            setAlerts(resp);
        }).catch(() => {
            setAlerts([]);
            showToast({
                message: t("administration.clientAlerts.retrieve-alerts-failed"),
                type: ToastType.ERROR,
            });
        }).finally(() => {
            setAlertsLoading(false);
        });
    }, [fetchAllAlerts, showToast, t]);

    const handleCreateAlert = async (newAlert: CreateAlertPayload) => {
        try {
            const createdAlert = await createAlert(newAlert);
            setAlerts(prevAlerts => [createdAlert, ...prevAlerts]);

            showToast({
                message: t('createAlertDialog.alertCreatedSuccess'),
                type: ToastType.SUCCESS,
            });
        } catch (error) {
            showToast({
                message: t('createAlertDialog.alertCreationFailed'),
                type: ToastType.ERROR,
            });
        }
    };

    const handleDeleteAlert = async (id: number) => {
        showToast({
            message: "",
            confirmMessage: t("administration.clientAlerts.confirm-delete-alert"),
            type: ToastType.CONFIRM,
            confirmCallback: async () => {
                try {
                    setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.id !== id));
                    await deleteAlert(id);
                    showToast({
                        message: t("administration.clientAlerts.delete-alert-success"),
                        type: ToastType.SUCCESS,
                    });
                } catch (error) {
                    // if delete fails, refetch alerts to restore state
                    fetchAllAlerts().then((resp) => setAlerts(resp));

                    showToast({
                        message: t("administration.clientAlerts.delete-alert-failed"),
                        type: ToastType.ERROR,
                    });
                }
            },
        });
    }

    const handleUpdateAlert = async (updateData: Partial<Alert>): Promise<Alert | undefined> => {
        try {
            const updatedAlert = await updateAlert(updateData);
            setAlerts((prevAlerts) => prevAlerts.map(alert => alert.id === updatedAlert.id ? updatedAlert : alert));

            showToast({
                message: t('createAlertDialog.alertUpdatedSuccess'),
                type: ToastType.SUCCESS,
            });

            return updatedAlert;
        } catch (error) {
            // if update fails, refetch alerts to restore state
            fetchAllAlerts().then(resp => setAlerts(resp));

            showToast({
                message: t('createAlertDialog.alertUpdateFailed'),
                type: ToastType.ERROR,
            });
        }
    };

    const handleMarkAlertAsResolved = async (alertId: number) => {
        try {
            await updateAlert({ id: alertId, active: false });

            setAlerts(prevAlerts => prevAlerts.filter(alert => alert.id !== alertId));
            showToast({
                message: t('administration.clientAlerts.mark-resolved-success'),
                type: ToastType.SUCCESS
            });
        } catch (error) {
            // if update fails, refetch alerts to restore state
            fetchAllAlerts().then(resp => setAlerts(resp));

            showToast({
                message: t('administration.clientAlerts.mark-resolved-failed'),
                type: ToastType.ERROR
            });
        }
    };

    return (
        <ClientAlertsContext.Provider
            value={{
                alerts,
                alertsLoading,
                handleDeleteAlert,
                handleUpdateAlert,
                handleCreateAlert,
                handleMarkAlertAsResolved,
            }}>
            {children}
        </ClientAlertsContext.Provider>
    );
};

export const useClientAlerts = (): ClientAlertsContextType => {
    const context = useContext(ClientAlertsContext);
    if (!context) {
        throw new Error("useClientAlerts must be used within a ClientAlertsProvider");
    }
    return context;
};
